import React, { useState, useEffect } from "react";
import AOS from "aos";
import API from "../api";
import SEO from "../components/SEO";

const StudentEnquiriesPage = () => {
  useEffect(() => {
    AOS.init({ duration: 700, once: true });
  }, []);

  // Ensure the page scrolls to top whenever this page is navigated to or the id changes
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window?.scrollTo) {
        // instant jump to top to avoid flashing at previous scroll position
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    } catch (e) {
      // fallback: simple assignment
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        // ignore in environments without window
      }
    }
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    student_id: "",
    type: "student_enquiries",
    subject: "",
    message: "",
  });
  const [success, setSuccess] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [lastPayload, setLastPayload] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setFile(f || null);
  };

  const extractSubmitError = (err, fallbackMsg) => {
    const data = err?.response?.data || {};
    const msg = data?.message || err?.message || fallbackMsg;
    const reason = data?.reason ? ` Reason: ${data.reason}` : "";
    return `${msg}${reason}`;
  };

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Name is required";
    if (!form.email.trim()) err.email = "Email is required";
    if (!form.subject.trim()) err.subject = "Subject is required";
    if (!form.message.trim()) err.message = "Message is required";
    if (!form.type.trim()) err.type = "Type is required";
    return err;
  };

  // Update the handleSubmit function in StudentEnquiriesPage.jsx
  const handleSubmit = async (e) => {
    e && e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setLoading(true);
    setSuccess(null);
    setErrors({});

    try {
      const response = await API.post('/complaints', {
        name: form.name,
        email: form.email,
        student_id: form.student_id,
        type: form.type,
        subject: form.subject,
        message: form.message
      });

      if (response?.data?.success !== true) {
        throw new Error(response?.data?.message || "There was an error sending your message.");
      }

      setSuccess(response.data.message || "Your message has been sent successfully! We'll get back to you soon.");
      setForm({
        name: "",
        email: "",
        student_id: "",
        type: "student_enquiries",
        subject: "",
        message: ""
      });
    } catch (err) {
      console.error(err);
      const msg = extractSubmitError(
        err,
        "There was an error sending your message. Please try again or contact support."
      );
      setErrors({ submit: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!lastPayload) return;
    setErrors({});
    setSuccess(null);
    setLoading(true);

    try {
      let res;
      if (lastPayload.type === 'formdata') {
        // Recreate the FormData object from stored raw data
        const formData = new FormData();
        Object.keys(lastPayload.formData).forEach((k) =>
          formData.append(k, lastPayload.formData[k])
        );
        formData.append('file', lastPayload.file);

        res = await API.post('/complaints', formData);
      } else {
        res = await API.post('/complaints', lastPayload.body, {
          headers: { 'Content-Type': 'application/json' }
        });
      }

      const data = await res.data;
      if (data?.success !== true) {
        throw new Error(data?.message || "There was an error submitting your enquiry.");
      }
      setSuccess(data.message || "Your submission has been received. We will respond within 3 business days.");
      setForm({ name: "", email: "", student_id: "", type: "student_enquiries", subject: "", message: "" });
      setFile(null);
      setLastPayload(null);
      setTimeout(() => setSuccess(null), 8000);
    } catch (err) {
      console.error(err);
      const msg = extractSubmitError(
        err,
        "There was an error submitting your enquiry. Please try again."
      );
      setErrors((s) => ({ ...s, submit: msg }));
    } finally {
      setLoading(false);
    }
  };

  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/student-enquiries';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    'name': 'Student Enquiries - Africa Research University',
    'description': 'Submit enquiries or complaints to Africa Research University student services.',
    'url': pageUrl
  };

  return (
    <section className="pt-1 pb-16 bg-gray-50 min-h-screen">
      <SEO title="Student Enquiries — Africa Research University" description="Submit enquiries or complaints to ARU student services." image="/assets/img/arulogo.png" url={pageUrl} jsonLd={jsonLd} />
      {/* <Hero slides={slidesData} interval={6000} height="260px" borderRadius="20px" hidden={true} /> */}

      <div className="container my-16 mx-auto bg-gray-100 px-4 py-12">
        <div className="max-w-3xl mx-auto rounded-lg bg-white border p-6" id="form" data-aos="fade-up">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Student Enquiries & Complaints</h2>
          <p className="text-sm text-gray-600 mb-6">Please provide as much detail as possible. Fields marked with * are required.</p>

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">{success}</div>
          )}

          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              <div className="font-medium">Submission error</div>
              <div className="text-sm mt-1">{errors.submit}</div>
              <div className="mt-2">
                <button type="button" onClick={handleRetry} disabled={loading} className="text-sm underline text-red-700 hover:text-red-900">Retry</button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Full name *</label>
                <input name="name" value={form.name} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md" />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Email *</label>
                <input name="email" value={form.email} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md" />
                {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Student ID (optional)</label>
              <input name="student_id" value={form.student_id} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select name="type" value={form.type} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md">
                  <option value="student_enquiries">Student Enquiries</option>
                  <option value="student_complaints">Student Complaints</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Subject *</label>
                <input name="subject" value={form.subject} onChange={handleChange} className="mt-1 px-2 py-1 block w-full border rounded-md" />
                {errors.subject && <p className="text-xs text-red-600 mt-1">{errors.subject}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Message *</label>
              <textarea name="message" value={form.message} onChange={handleChange} rows={6} className="mt-1 px-2 py-1 block w-full border rounded-md"></textarea>
              {errors.message && <p className="text-xs text-red-600 mt-1">{errors.message}</p>}
            </div>

            <div style={{ display: 'none' }}>
              <label className="block text-sm font-medium text-gray-700">Attach file (optional)</label>
              <input type="file" onChange={handleFileChange} className="mt-1 px-2 py-1 block w-full text-sm text-gray-700" />
              {file && <p className="text-xs text-gray-600 mt-1">Selected: {file.name}</p>}
            </div>

            <div className="flex items-center justify-between">
              <button type="submit" disabled={loading} className={`bg-green-600 text-white px-4 py-2 rounded-md transition ${loading ? 'opacity-60 cursor-not-allowed' : 'hover:bg-green-700'}`}>
                {loading ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={() => { setForm({ name: "", email: "", student_id: "", type: "student_enquiries", subject: "", message: "" }); setFile(null); }} disabled={loading} className="text-md text-gray-600 hover:text-gray-800">Reset</button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default StudentEnquiriesPage;
